import { Link } from 'react-router-dom'

import type { OrderChannel, OrderCustomerType, OrderRow, OrderSort } from '@/api/revenueLeakage'
import { formatCurrency, formatInteger, type FormatOptions } from '@/design'

import { UNDEFINED } from '../../list/cells'
import type { DescribeReason, ListColumn, NotAvailable, ReasonRule } from '../../list/nullState'

/*
 * The Orders tab's columns. Every row is a synced, completed order, so no column is ever
 * missing for a reason: a null (no payment recorded) is undefined for that order, a dash.
 * Money is the row's own currency — the API lists each order as it was recorded.
 */

/** No column of the orders list is ever missing for a reason the API names. */
export type OrderReason = never

/** What a cell needs beyond the row: the tenant's locale and timezone. */
export interface OrderCellContext {
  notAvailable: NotAvailable<OrderRow, OrderReason>
  money: FormatOptions
  timezone: string
}

export type OrderColumnGroup = 'order' | 'money' | 'source'

export const GROUP_LABELS: Record<OrderColumnGroup, string> = {
  order: 'Order',
  money: 'Money',
  source: 'Payment and source',
}

export interface OrderColumn extends ListColumn<OrderRow, OrderCellContext> {
  group: OrderColumnGroup
  sort?: OrderSort
}

export const unavailableReason: ReasonRule<OrderRow, OrderReason> = () => null

export const describeReason: DescribeReason<OrderReason> = (reason) => ({ label: reason, why: reason })

/** Each channel as the page names it; the two fixed values say what they mean. */
export const CHANNEL_LABELS: Record<OrderChannel, string> = {
  meta_paid: 'Meta ads',
  facebook_organic: 'Facebook (organic)',
  instagram_organic: 'Instagram (organic)',
  google_paid: 'Google ads',
  google_organic: 'Google search',
  email: 'Email',
  push: 'Push',
  referral: 'Referral',
  direct: 'Direct',
  other: 'Other',
  unattributed: 'No touch credited',
  unclassified: 'Not classified yet',
}

export const CUSTOMER_TYPE_LABELS: Record<OrderCustomerType, string> = {
  new: 'New customer',
  returning: 'Returning customer',
  guest: 'Guest checkout',
}

/** A competition's page, from a row's competition link. */
export function competitionHref(competitionId: string): string {
  return `/analytics/competitions/${encodeURIComponent(competitionId)}`
}

function amount(value: number, row: OrderRow, context: OrderCellContext): string {
  return formatCurrency(value, { ...context.money, currency: row.currency })
}

function completed(instant: string, { money, timezone }: OrderCellContext): string {
  return new Date(instant).toLocaleString(money.locale ?? 'en-GB', {
    timeZone: timezone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function Competitions({ row }: { row: OrderRow }) {
  if (row.competitions.length === 0 && row.unsyncedCompetitionLines === 0) return UNDEFINED
  return (
    <span className="rvo-competitions">
      {row.competitions.map((competition) => (
        <Link key={competition.competitionId} to={competitionHref(competition.competitionId)}>
          {competition.name}
        </Link>
      ))}
      {row.unsyncedCompetitionLines > 0 ? (
        <span className="list-muted">
          {row.unsyncedCompetitionLines === 1 ? '1 competition' : `${row.unsyncedCompetitionLines} competitions`} not synced yet
        </span>
      ) : null}
    </span>
  )
}

function payment(row: OrderRow) {
  const parts = [row.paymentProvider, row.paymentMethod].filter((part): part is string => part !== null && part !== '')
  return parts.length > 0 ? parts.join(' · ') : UNDEFINED
}

/** Every column the list can show, in display order. The first is always shown. */
export const ORDER_COLUMNS: readonly OrderColumn[] = [
  { key: 'external_order_id', label: 'Order', group: 'order', byDefault: true, cell: (row) => `#${row.externalOrderId}` },
  {
    key: 'completed_at',
    label: 'Completed',
    group: 'order',
    sort: 'completed_at',
    byDefault: true,
    cell: (row, context) => completed(row.completedAt, context),
  },
  { key: 'competitions', label: 'Competitions', group: 'order', byDefault: true, cell: (row) => <Competitions row={row} /> },
  { key: 'tickets', label: 'Tickets', group: 'order', sort: 'tickets', byDefault: true, align: 'end', cell: (row) => formatInteger(row.tickets) },
  { key: 'gross', label: 'Gross', group: 'money', sort: 'gross', byDefault: true, align: 'end', cell: (row, context) => amount(row.gross, row, context) },
  { key: 'net', label: 'Net before refunds', group: 'money', sort: 'net', byDefault: false, align: 'end', cell: (row, context) => amount(row.net, row, context) },
  {
    key: 'refunded',
    label: 'Refunded',
    group: 'money',
    sort: 'refunded',
    byDefault: true,
    align: 'end',
    cell: (row, context) => (row.refunded === 0 ? UNDEFINED : amount(row.refunded, row, context)),
  },
  { key: 'payment', label: 'Payment', group: 'source', byDefault: true, cell: payment },
  { key: 'channel', label: 'Channel', group: 'source', byDefault: true, cell: (row) => CHANNEL_LABELS[row.channel] ?? row.channel },
  { key: 'customer_type', label: 'Customer', group: 'source', byDefault: true, cell: (row) => CUSTOMER_TYPE_LABELS[row.customerType] ?? row.customerType },
]

export const COLUMN_CHOICE = {
  storageKey: 'ci.revenue.orders.columns',
  keys: ORDER_COLUMNS.map((column) => column.key),
  defaults: ORDER_COLUMNS.filter((column) => column.byDefault).map((column) => column.key),
} as const
